import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellRing, Volume2, VolumeX } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import api from '@/services/api';
import { formatDate } from '@/utils/dateFormat';
import { ORDER_STATUS, money } from './orderStatus';

const SEEN_KEY = 'hq_online_orders_seen_at';
const SOUND_KEY = 'hq_online_orders_sound';

const chime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.18].forEach((delay, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = i ? 1318 : 988;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + delay + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + 0.5);
      o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + delay); o.stop(ctx.currentTime + delay + 0.55);
    });
  } catch (e) { /* autoplay blocked — badge still shows */ }
};

export const OnlineOrderBell = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [unseen, setUnseen] = useState(0);
  const [sound, setSound] = useState(() => localStorage.getItem(SOUND_KEY) !== 'off');
  const lastSeen = useRef(localStorage.getItem(SEEN_KEY) || '');

  const poll = useCallback(async () => {
    try {
      const res = await api.get('/ecom-admin/alerts');
      const d = res.data;
      setData(d);
      if (d.latest_order_at && d.latest_order_at > lastSeen.current) {
        const fresh = d.recent.filter((o) => o.created_at > lastSeen.current).length;
        setUnseen(fresh || 1);
        if (sound && lastSeen.current) chime();
        if (!lastSeen.current) { lastSeen.current = d.latest_order_at; localStorage.setItem(SEEN_KEY, d.latest_order_at); setUnseen(0); }
      }
    } catch (e) { /* ignore */ }
  }, [sound]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 60000);
    return () => clearInterval(t);
  }, [poll]);

  const markSeen = () => {
    if (data?.latest_order_at) { lastSeen.current = data.latest_order_at; localStorage.setItem(SEEN_KEY, data.latest_order_at); }
    setUnseen(0);
  };
  const toggleSound = (e) => { e.stopPropagation(); const n = !sound; setSound(n); localStorage.setItem(SOUND_KEY, n ? 'on' : 'off'); };

  if (!data) return null;
  const pending = data.new_orders || 0;

  return (
    <div className="fixed top-3 right-4 z-40" data-testid="online-order-bell">
      <Popover onOpenChange={(o) => o && markSeen()}>
        <PopoverTrigger asChild>
          <button type="button" className={`relative h-10 w-10 rounded-full bg-white border shadow-md flex items-center justify-center hover:bg-gray-50 ${unseen ? 'ring-2 ring-emerald-500' : ''}`} data-testid="bell-button" title="Online orders">
            {unseen ? <BellRing className="h-5 w-5 text-emerald-600 animate-[wiggle_0.6s_ease-in-out_infinite]" /> : <Bell className="h-5 w-5 text-gray-600" />}
            {pending > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[11px] font-bold flex items-center justify-center" data-testid="bell-count">{pending}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96 p-0">
          <div className="px-3 py-2 border-b flex items-center justify-between">
            <div className="text-sm font-semibold">Online orders · {pending} awaiting confirmation</div>
            <button type="button" onClick={toggleSound} className="text-gray-500 hover:text-gray-800" data-testid="bell-sound-toggle" title={sound ? 'Mute alert sound' : 'Enable alert sound'}>
              {sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto divide-y">
            {data.recent.slice(0, 6).map((o) => {
              const st = ORDER_STATUS[o.status] || { label: o.status, tone: 'bg-gray-100 text-gray-700' };
              return (
                <button type="button" key={o.id} onClick={() => navigate(`/hq/ecommerce/orders?open=${o.id}`)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50" data-testid={`bell-order-${o.order_number}`}>
                  <div className="flex justify-between"><span className="font-medium">{o.order_number}</span><span className="font-semibold">{money(o.grand_total)}</span></div>
                  <div className="flex justify-between text-xs text-gray-500 mt-0.5"><span>{o.customer_name} · {formatDate(o.created_at)}</span><Badge className={`${st.tone} border-0 text-[10px]`}>{st.label}</Badge></div>
                </button>
              );
            })}
          </div>
          <button type="button" onClick={() => navigate('/hq/ecommerce/orders?status=paid')} className="w-full text-center text-xs text-indigo-600 py-2 border-t hover:bg-gray-50" data-testid="bell-view-all">Open order management</button>
        </PopoverContent>
      </Popover>
    </div>
  );
};
